import { useCallback, useId, useState, type ReactNode } from "react";
import type { CallView, ConversationOutcome, ConversationRecordingFilter, ConversationRow } from "@gioia365/contracts";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTab } from "../api/queries";
import { Button } from "../design/components/Button";
import { Checkbox } from "../design/components/Checkbox";
import { DataTable } from "../design/components/DataTable";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { Segmented } from "../design/components/Segmented";
import { Select } from "../design/components/Select";
import { StatusPill } from "../design/components/StatusPill";
import { useToast } from "../design/components/Toast";
import { formatClockDuration, formatNumericDateTime } from "../i18n/format";
import { useT, type Translate } from "../i18n/locale";
import { outcomeLabel } from "../leads/lead-display";
import { useResultSearch } from "../leads/lead-list-hooks";
import { useLeadView } from "../leads/lead-queries";
import { NoteComposer } from "../leads/timeline/NoteComposer";
import { canActOnLeadOf, conversationScopeOf, useAccess, type Access } from "../session/access";
import type { TabContext } from "../session/tab-context";
import { callQualityLabel, ringDurationText, outcomeText, RECORDING_LINES, recordingLineOf, type RecordingLine } from "../telephony/call-display";
import { RecordingPlayer } from "../telephony/RecordingPlayer";
import { ConversationsPeriod, ConversationsProblem, ConversationsSearchField, type ConversationsTabProps } from "./ConversationParts";
import { conversationPeopleQuery, conversationsPagesQuery, useAddCallNote } from "./conversation-queries";
import { conversationsListQueryOf, directionParamOf, validateConversationsSearch, type ConversationsSearch } from "./conversation-search";
import { EmailTab } from "./EmailTab";
import { whatsAppUnreadQuery } from "./whatsapp-queries";
import { WhatsAppTab } from "./WhatsAppTab";
import "../design/components/page-header.css";
import "../leads/lead-list.css";
// Il riquadro dell'appunto (`NoteComposer`) ha le sue regole con la Timeline.
import "../leads/timeline/timeline.css";
import "../telephony/recording.css";
import "./conversations.css";

// «Conversazioni» (piano M4, Task 19, D14; mock-up `conversazioni.html` e `initConversations` di `mockup.js`; `PRODUCT_SPEC.md`
// §6, «Vista Conversazioni»): le telefonate nel perimetro di chi guarda, dalla più recente, una riga per telefonata con quando,
// il Lead e il salone, la direzione, l'esito in due passi, la durata, chi ha chiamato (per chi vede le chiamate di tutti),
// l'owner di allora con quello di oggi se è cambiato e lo stato della registrazione con gli appunti. Il periodo, i filtri e la
// ricerca stanno nell'indirizzo (`conversation-search.ts`). «Ascolta» (o «Apri», senza audio da ascoltare) apre la riga: il
// lettore della Timeline (`RecordingPlayer`, mai una copia), gli appunti dal più vecchio, «Aggiungi un appunto» per chi può
// modificare il Lead (altrimenti il perché) e «Apri il Lead». Sono gli stessi eventi della Timeline, con le stesse parole
// (`call-display.ts`). Sotto i 720 px di contenuto ogni riga è una scheda, senza scorrimento di lato. Dal piano M6 (R3, passo 12b)
// la seconda scheda, «WhatsApp» (`WhatsAppTab.tsx`, `?vista=whatsapp` come nel mock-up), con sul pulsante il numero da leggere, lo
// stesso del menu (D24); periodo, «Di chi» e ricerca restano nell'indirizzo passando da una scheda all'altra. Dall'ingresso dei Lead (I5,
// D5) la terza, «Email» (`EmailTab.tsx`, `?vista=email`): gli eventi delle email di GoHighLevel, in sola lettura.

/** «Esito» nell'ordine della postazione: il primo passo, poi le voci di «Com'è andata?». */
const OUTCOMES: readonly ConversationOutcome[] = ["answered", "no_answer", "wrong_number", "call_back", "not_interested", "do_not_call", "disco_booked", "demo_booked", "other"];

/** «Registrazione» nell'ordine del mock-up (`REC_TEXT`), senza «Si registra», che è soltanto delle chiamate in corso. */
const RECORDING_FILTERS: readonly ConversationRecordingFilter[] = ["available", "processing", "expired", "missing", "error", "none"];

/** Lo stesso indirizzo senza i valori tolti: un filtro tornato a «Tutti» non resta scritto. */
function compact(search: ConversationsSearch): ConversationsSearch {
  return Object.fromEntries(Object.entries(search).filter(([, value]) => value !== undefined)) as ConversationsSearch;
}

/** Il nome di chi ha fatto la cosa, come allora. */
function actorName(t: Translate, name: string | undefined): string {
  return name?.trim() || t("timeline.unknownAuthor");
}

/** I motivi di fine di una chiamata della telefonia mai arrivata al Lead (`finalFacts` del dominio: non conta). */
const NOT_COUNTED_REASONS: ReadonlySet<string> = new Set(["failed", "not_connected", "destination_not_allowed"]);

const isLive = (call: CallView) => call.liveState !== "ended";

/**
 * Una telefonata finita che non conta, con la regola della Timeline (`valid` falso: un errore tecnico, anche importata da Close),
 * oppure finita con un motivo che la rende non valida: si dice «Non riuscita, non conta», senza la riga della registrazione.
 */
const notCounted = (row: ConversationRow) => !isLive(row.call) && (!row.valid || (row.call.endedReason !== null && NOT_COUNTED_REASONS.has(row.call.endedReason)));

/** L'esito come nella Timeline: «In corso», una chiamata che non conta, «Risposta · Richiamare», un esito non noto. */
function outcomeOf(t: Translate, row: ConversationRow): string {
  const { call } = row;
  if (isLive(call)) return t("timeline.call.live");
  if (call.quality?.numberProblem && call.outcome === "wrong_number") return outcomeText(t, call.outcome, call.answeredOutcome, null);
  if (callQualityLabel(t, call.quality)) return callQualityLabel(t, call.quality)!;
  if (notCounted(row)) return t("timeline.call.failed");
  if (call.outcome !== null) return outcomeText(t, call.outcome, call.answeredOutcome, null);
  return t("timeline.call.unknownOutcome");
}

/** La riga della registrazione: le parole della Timeline, dall'origine della telefonata; nessuna per una chiamata che non conta. */
function lineOf(row: ConversationRow): RecordingLine | null {
  if (notCounted(row)) return null;
  const origin = row.fromClose ? "close_import" : row.manual ? "manual" : "telephony";
  return recordingLineOf({ live: isLive(row.call), recording: row.recording, canListen: row.canListen, origin });
}

export function ConversationsPage() {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  // Di nuovo dalla forma, come l'elenco dei Lead: un valore che `validateConversationsSearch` scarta resta grezzo nella ricerca
  // della rotta (il router unisce quella dell'indirizzo), e non deve arrivare al server né restare nell'indirizzo.
  const search = validateConversationsSearch(useSearch({ from: "/autenticato/guscio/azienda/conversazioni" }));
  const navigate = useNavigate({ from: "/conversazioni" });
  const canView = conversationScopeOf(access) !== "none";
  const whatsapp = search.vista === "whatsapp";
  const email = search.vista === "email";
  // Il numero da leggere sul pulsante della scheda «WhatsApp», lo stesso del menu (D24).
  const unread = useQuery({ ...whatsAppUnreadQuery(tab), enabled: canView });
  const unreadCount = unread.data?.count ?? 0;

  const update = useCallback(
    (change: Partial<ConversationsSearch>) => void navigate({ search: (previous) => compact({ ...validateConversationsSearch(previous), ...change }), replace: true }),
    [navigate],
  );
  const writeQ = useCallback((next: string | undefined) => update({ q: next }), [update]);

  const tabs = [
    { value: "calls", label: t("conversations.calls") },
    { value: "whatsapp", label: t("conversations.whatsappTab"), ...(unreadCount > 0 ? { count: String(unreadCount) } : {}) },
    { value: "email", label: t("conversations.emailTab") },
  ];

  let body: ReactNode;
  if (!canView) {
    body = (
      <div className="g-conversations__results">
        <Reason tone="warning">{t("errors.permission_denied")}</Reason>
      </div>
    );
  } else if (whatsapp) body = <WhatsAppTab search={search} update={update} writeQ={writeQ} />;
  else if (email) body = <EmailTab search={search} update={update} writeQ={writeQ} />;
  else body = <CallsTab search={search} update={update} writeQ={writeQ} />;

  return (
    <div className="g-conversations">
      <div className="g-page-head g-conversations__head">
        <div>
          <p className="g-page-head__eyebrow">{t("shell.nav.conversations")}</p>
          <h1 className="g-page-head__title">{t("conversations.title")}</h1>
          <p className="g-conversations__intro">{t("conversations.intro")}</p>
        </div>
        {canView ? (
          <Segmented
            label={t("conversations.tabsLabel")}
            options={tabs}
            value={search.vista ?? "calls"}
            onChange={(value) => update({ vista: value === "whatsapp" || value === "email" ? value : undefined })}
          />
        ) : null}
      </div>
      {body}
    </div>
  );
}

/** La scheda «Telefonate» (piano M4, Task 19): vedi in cima al file. */
function CallsTab({ search, update, writeQ }: ConversationsTabProps) {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const view = useLeadView();
  const baseId = useId();
  // «Di chi» e «Chi ha chiamato» soltanto per chi vede le chiamate di tutti, con la regola del server.
  const byPerson = conversationScopeOf(access) === "all";
  const query = conversationsListQueryOf(search, byPerson);
  const pages = useInfiniteQuery({ ...conversationsPagesQuery(tab, query, view), placeholderData: keepPreviousData });
  const people = useQuery(conversationPeopleQuery(tab));
  const timeZone = access.context.settings.timeZone;
  const shown = pages.data && !pages.isPlaceholderData ? pages.data.pages.flatMap((page) => page.rows) : null;
  const [openId, setOpenId] = useState<string | null>(null);
  const { text, setText } = useResultSearch(search.q, writeQ);

  const peopleOptions = (people.data ?? []).map((person) => ({ value: person.membershipId, label: person.displayName }));
  // Una persona dell'indirizzo che non è fra le scelte (un link di prima) resta scelta, con il nome che le righe le danno.
  const firstRow = shown?.[0];
  const whoOptions = [{ value: "all", label: t("conversations.wholeTeam") }, ...peopleOptions];
  if (search.chi !== undefined && !whoOptions.some((option) => option.value === search.chi)) whoOptions.push({ value: search.chi, label: firstRow?.call.author.displayName ?? search.chi });
  const ownerOptions = [{ value: "all", label: t("conversations.anyone") }, ...peopleOptions];
  if (search.owner !== undefined && !ownerOptions.some((option) => option.value === search.owner)) ownerOptions.push({ value: search.owner, label: firstRow?.ownerAtCall?.displayName ?? search.owner });
  const directionOptions = [
    { value: "all", label: t("conversations.all") },
    { value: "outbound", label: t("conversations.outbound") },
    { value: "inbound", label: t("conversations.inbound") },
  ];
  const outcomeOptions = [{ value: "all", label: t("conversations.anyone") }, ...OUTCOMES.map((outcome) => ({ value: outcome, label: outcomeLabel(t, outcome) }))];
  const recordingOptions = [{ value: "all", label: t("conversations.all") }, ...RECORDING_FILTERS.map((filter) => ({ value: filter, label: t(`conversations.recordingFilters.${filter}`) }))];

  let results: ReactNode;
  if (shown === null) {
    results = pages.isError ? (
      <ConversationsProblem error={pages.error} onRetry={() => void pages.refetch()} />
    ) : (
      <p className="g-conversations__loading" role="status">
        {t("common.loading")}
      </p>
    );
  } else if (shown.length === 0) {
    results = (
      <div className="g-conversations__empty">
        <p>
          <strong>{t("conversations.empty")}</strong>
        </p>
        <p>{t("conversations.emptyHint")}</p>
      </div>
    );
  } else {
    results = (
      <>
        {pages.isError ? <ConversationsProblem error={pages.error} onRetry={() => void pages.refetch()} /> : null}
        <div className="g-conversations__table">
          <DataTable caption={t("conversations.caption")}>
            <thead>
              <tr>
                <th scope="col">{t("conversations.columns.when")}</th>
                <th scope="col">{t("conversations.columns.lead")}</th>
                <th scope="col">{t("conversations.columns.direction")}</th>
                <th scope="col">{t("conversations.columns.outcome")}</th>
                <th scope="col">{t("conversations.columns.duration")}</th>
                {byPerson ? <th scope="col">{t("conversations.columns.caller")}</th> : null}
                <th scope="col">{t("conversations.columns.ownerThen")}</th>
                <th scope="col">{t("conversations.columns.recording")}</th>
                <th scope="col">
                  <span className="g-visually-hidden">{t("conversations.columns.action")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <ConversationRowView
                  key={row.call.id}
                  row={row}
                  tab={tab}
                  access={access}
                  byPerson={byPerson}
                  timeZone={timeZone}
                  open={openId === row.call.id}
                  onToggle={() => setOpenId((current) => (current === row.call.id ? null : row.call.id))}
                />
              ))}
            </tbody>
          </DataTable>
        </div>
        {pages.hasNextPage ? (
          <div>
            <Button onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage}>
              {t("conversations.more")}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      <div className="g-conversations__toolbar">
        <ConversationsPeriod search={search} update={update} />
        <div className="g-conversations__filters">
          {byPerson ? (
            <Select id={`${baseId}-who`} label={t("conversations.who")} value={search.chi ?? "all"} options={whoOptions} onChange={(value) => update({ chi: value === "all" ? undefined : value })} />
          ) : null}
          <Select
            id={`${baseId}-direction`}
            label={t("conversations.direction")}
            value={query.direction ?? "all"}
            options={directionOptions}
            onChange={(value) => update({ direzione: directionParamOf(value) })}
          />
          <Select
            id={`${baseId}-outcome`}
            label={t("conversations.outcome")}
            value={search.esito ?? "all"}
            options={outcomeOptions}
            onChange={(value) => update({ esito: OUTCOMES.find((outcome) => outcome === value) })}
          />
          <Select id={`${baseId}-owner`} label={t("conversations.ownerThen")} value={search.owner ?? "all"} options={ownerOptions} onChange={(value) => update({ owner: value === "all" ? undefined : value })} />
          <Select
            id={`${baseId}-recording`}
            label={t("conversations.recording")}
            value={search.registrazione ?? "all"}
            options={recordingOptions}
            onChange={(value) => update({ registrazione: RECORDING_FILTERS.find((filter) => filter === value) })}
          />
          <Checkbox
            id={`${baseId}-audio`}
            className="g-conversations__audio"
            label={t("conversations.audioOnly")}
            checked={search.audio === 1}
            onChange={(checked) => update({ audio: checked ? 1 : undefined })}
          />
        </div>
        <ConversationsSearchField id={`${baseId}-search`} text={text} onChange={setText} />
      </div>
      <div className="g-conversations__results" aria-busy={shown === null && !pages.isError ? true : undefined}>
        {results}
      </div>
    </>
  );
}

type RowProps = {
  row: ConversationRow;
  tab: TabContext;
  access: Access;
  byPerson: boolean;
  timeZone: string;
  open: boolean;
  onToggle: () => void;
};

function ConversationRowView({ row, tab, access, byPerson, timeZone, open, onToggle }: RowProps) {
  const t = useT();
  // «Ascolta», «Apri» e «Chiudi» dicono di quale Lead sono: il nome come descrizione.
  const nameId = useId();
  const detailId = useId();
  const { call, lead } = row;
  const live = isLive(call);
  const inbound = call.direction === "inbound";
  const line = lineOf(row);
  const lineView = line === null ? null : RECORDING_LINES[line];
  const ownerId = lead?.currentOwner?.membershipId ?? null;
  // Il Lead si apre, e riceve appunti, con le regole del server (`canActOnLead`): la telefonata può stare su un Lead che intanto
  // è passato a un collega, fuori dai Lead che chi guarda vede.
  const canOpenLead = lead !== null && canActOnLeadOf(access, "view", ownerId);
  const canAddNotes = canOpenLead && canActOnLeadOf(access, "update", ownerId);
  // «oggi: …» quando l'owner di adesso è un'altra persona di quello di allora (anche nessuno, o non noto per le chiamate di
  // Close): lo dice il server, che confronta le persone e non i nomi (`ownerChanged`).
  const ownerThen = row.ownerAtCall?.displayName ?? null;
  const ownerChanged = lead !== null && row.ownerChanged;
  const ownerNow = lead?.currentOwner?.displayName ?? t("leadCard.noOwner");
  const columns = byPerson ? 9 : 8;

  let actionLabel: ReactNode;
  if (open) actionLabel = t("conversations.closeRow");
  else if (line === "available")
    actionLabel = (
      <>
        <Icon name="play" />
        {t("conversations.listen")}
      </>
    );
  else actionLabel = t("conversations.open");

  return (
    <>
      <tr data-open={open ? "true" : undefined}>
        <td data-label={t("conversations.columns.when")}>{formatNumericDateTime(timeZone, call.startedAt)}</td>
        <th scope="row">
          {lead !== null && canOpenLead ? (
            <Link to="/lead/$leadId" params={{ leadId: lead.id }} id={nameId}>
              {lead.displayName ?? "—"}
            </Link>
          ) : (
            <span id={nameId} className="g-conversations__lead">
              {lead?.displayName ?? "—"}
            </span>
          )}
          {lead?.salonName ? <span className="g-table__sub">{lead.salonName}</span> : null}
        </th>
        <td data-label={t("conversations.columns.direction")}>
          <span className="g-pill" data-tone={inbound ? "accent" : "neutral"}>
            <Icon name={inbound ? "down" : "up"} />
            {inbound ? t("conversations.inbound") : t("conversations.outbound")}
          </span>
          {row.manual ? <span className="g-table__sub">{t("conversations.manual")}</span> : null}
          {row.fromClose ? <span className="g-table__sub">{t("timeline.fromClose")}</span> : null}
        </td>
        <td data-label={t("conversations.columns.outcome")}>{outcomeOf(t, row)}</td>
        <td data-label={t("conversations.columns.duration")}>
          {call.quality ? <span className="g-table__sub">{ringDurationText(t, call.quality)}</span> : null}
          {live ? (
            <span className="g-pill" data-tone="accent">
              {t("conversations.liveDuration")}
            </span>
          ) : call.durationSeconds === null ? (
            "—"
          ) : (
            formatClockDuration(call.durationSeconds)
          )}
        </td>
        {byPerson ? <td data-label={t("conversations.columns.caller")}>{actorName(t, call.author.displayName)}</td> : null}
        <td data-label={t("conversations.columns.ownerThen")}>
          {ownerThen ?? "—"}
          {ownerChanged ? <span className="g-table__sub">{t("conversations.ownerToday", { name: ownerNow })}</span> : null}
        </td>
        <td data-label={t("conversations.columns.recording")}>
          {lineView ? (
            <StatusPill tone={lineView.tone} icon={lineView.icon}>
              {t(lineView.text)}
            </StatusPill>
          ) : (
            "—"
          )}
          {row.notesCount > 0 ? (
            <span className="g-table__sub">{row.notesCount === 1 ? t("conversations.notesOne") : t("conversations.notesMany", { count: row.notesCount })}</span>
          ) : null}
        </td>
        <td className="g-conversations__action">
          <Button aria-expanded={open} aria-controls={open ? detailId : undefined} aria-describedby={nameId} onClick={onToggle}>
            {actionLabel}
          </Button>
        </td>
      </tr>
      {open ? (
        <tr className="g-conversations__detail" id={detailId}>
          <td colSpan={columns}>
            <ConversationDetail row={row} tab={tab} timeZone={timeZone} line={line} canOpenLead={canOpenLead} canAddNotes={canAddNotes} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

type DetailProps = {
  row: ConversationRow;
  tab: TabContext;
  timeZone: string;
  line: RecordingLine | null;
  canOpenLead: boolean;
  canAddNotes: boolean;
};

/** La riga aperta: il lettore o lo stato della registrazione a sinistra, gli appunti e «Apri il Lead» a destra. */
function ConversationDetail({ row, tab, timeZone, line, canOpenLead, canAddNotes }: DetailProps) {
  const t = useT();
  const toast = useToast();
  const view = useLeadView();
  const addNote = useAddCallNote(tab, view);
  const { call, lead } = row;
  const lineView = line === null ? null : RECORDING_LINES[line];
  const expiresAt = line === "available" ? (row.recording?.expiresAt ?? null) : null;

  async function save(body: string) {
    if (lead === null) return;
    await addNote.mutateAsync({ leadId: lead.id, callId: call.id, body });
    toast.show(t("conversations.noteSaved"), "success");
  }

  let notesFooter: ReactNode = null;
  if (lead !== null && !canOpenLead) notesFooter = <Reason>{t("conversations.leadOutOfSight")}</Reason>;
  else if (lead !== null && canAddNotes)
    notesFooter = (
      <NoteComposer
        className="g-conversations__composer"
        texts={{ label: "conversations.addNote", submit: "conversations.saveNote", saving: "conversations.savingNote", required: "conversations.noteRequired", hint: "conversations.noteHint" }}
        onSave={save}
      />
    );
  else if (lead !== null) notesFooter = <Reason>{t("conversations.notesReadOnly")}</Reason>;

  return (
    <div className="g-conversations__detail-grid">
      <div className="g-conversations__listen">
        {line === "available" ? (
          <RecordingPlayer tab={tab} callId={call.id} durationSeconds={row.recording?.durationSeconds ?? call.durationSeconds} />
        ) : lineView ? (
          <p className="g-recording-state">
            <StatusPill tone={lineView.tone} icon={lineView.icon}>
              {t(lineView.text)}
            </StatusPill>
          </p>
        ) : null}
        {expiresAt ? <p className="g-conversations__hint">{t("conversations.expiresAt", { when: formatNumericDateTime(timeZone, expiresAt) })}</p> : null}
        {row.manual ? <p className="g-conversations__hint">{t("conversations.manualHint")}</p> : null}
      </div>
      <div className="g-conversations__notes">
        <h2 className="g-conversations__notes-title">{t("conversations.notesTitle")}</h2>
        {row.notes.length > 0 ? (
          <ol className="g-conversations__note-list">
            {row.notes.map((note) => {
              const by = ` · ${actorName(t, note.actor.displayName)}`;
              return (
                <li key={note.id}>
                  <p className="g-conversations__note-meta">
                    <strong>{formatNumericDateTime(timeZone, note.occurredAt)}</strong>
                    {by}
                  </p>
                  <p className="g-conversations__note-body">{note.body}</p>
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="g-conversations__hint">{t("conversations.noNotes")}</p>
        )}
        {notesFooter}
        {lead !== null && canOpenLead ? (
          <div>
            <Link to="/lead/$leadId" params={{ leadId: lead.id }} className="g-button">
              {t("conversations.openLead")}
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}
